import api from '../../../lib/api';
import type {
  CreateAutoCallRuleInput,
  CreateVoiceTemplateInput,
  ElevenLabsModel,
  ElevenLabsVoice,
  RecommendedVoicesResponse,
  SharedVoicesResponse,
  UpdateAutoCallRuleInput,
  UpdateVoiceTemplateInput,
  VoiceLibraryFilters,
  VoicePreviewInput,
  VoicePreviewResponse,
  VoiceTemplate,
} from '../types';

export const voiceApi = {
  getVoices: () => api.get<ElevenLabsVoice[]>('/voice/voices').then((res) => res.data),

  getModels: () => api.get<ElevenLabsModel[]>('/voice/models').then((res) => res.data),

  getVoiceLibrary: (filters: VoiceLibraryFilters = {}) =>
    api.get<SharedVoicesResponse>('/voice/library', { params: filters }).then((res) => res.data),

  getRecommendedVoices: (organizationId: string) =>
    api
      .get<RecommendedVoicesResponse>('/voice/recommended', {
        params: { organizationId },
      })
      .then((res) => res.data),

  generatePreview: async (input: VoicePreviewInput): Promise<Blob> => {
    const response = await api.post('/voice/preview', input, {
      responseType: 'blob',
    });
    return response.data;
  },

  generateAndSavePreview: async (input: VoicePreviewInput): Promise<VoicePreviewResponse> => {
    const response = await api.post<VoicePreviewResponse>('/voice/preview', {
      ...input,
      saveToTemplate: true,
    });
    return response.data;
  },

  getTemplates: (organizationId: string) =>
    api
      .get<VoiceTemplate[]>('/voice/templates', { params: { organizationId } })
      .then((res) => res.data),

  getTemplate: (id: string, organizationId: string) =>
    api
      .get<VoiceTemplate>(`/voice/templates/${id}`, { params: { organizationId } })
      .then((res) => res.data),

  createTemplate: (input: CreateVoiceTemplateInput) =>
    api.post<VoiceTemplate>('/voice/templates', input).then((res) => res.data),

  updateTemplate: (id: string, organizationId: string, input: UpdateVoiceTemplateInput) =>
    api
      .patch<VoiceTemplate>(`/voice/templates/${id}`, input, { params: { organizationId } })
      .then((res) => res.data),

  deleteTemplate: (id: string, organizationId: string) =>
    api.delete(`/voice/templates/${id}`, { params: { organizationId } }),

  duplicateTemplate: (id: string, organizationId: string, newName: string) =>
    api
      .post<VoiceTemplate>(`/voice/templates/${id}/duplicate`, undefined, {
        params: { organizationId, newName },
      })
      .then((res) => res.data),

  triggerAutoCall: (organizationId: string, leadId: string, templateId?: string) =>
    api
      .post<{ enqueued: boolean; reason?: string }>('/voice/auto-call/test', undefined, {
        params: { organizationId, leadId, ...(templateId && { templateId }) },
      })
      .then((res) => res.data),

  getAutoCallRule: (organizationId: string) =>
    api.get('/voice/auto-call/rules', { params: { organizationId } }).then((res) => res.data),

  createAutoCallRule: (input: CreateAutoCallRuleInput) =>
    api.post('/voice/auto-call/rules', input).then((res) => res.data),

  updateAutoCallRule: (organizationId: string, input: UpdateAutoCallRuleInput) =>
    api
      .patch('/voice/auto-call/rules', input, { params: { organizationId } })
      .then((res) => res.data),

  deleteAutoCallRule: (organizationId: string) =>
    api.delete('/voice/auto-call/rules', { params: { organizationId } }),

  getAutoCallLogs: (params: {
    organizationId: string;
    leadId?: string;
    page?: number;
    limit?: number;
  }) => api.get('/voice/auto-call/logs', { params }).then((res) => res.data),

  getAutoCallStats: (organizationId: string) =>
    api.get('/voice/auto-call/stats', { params: { organizationId } }).then((res) => res.data),

  getLeadCallStatus: (organizationId: string, leadId: string) =>
    api
      .get<AutoCallStatus | null>('/voice/auto-call/lead-status', {
        params: { organizationId, leadId },
      })
      .then((res) => res.data),

  cancelAutoCall: (logId: string, organizationId: string) =>
    api
      .post<{ cancelled: boolean; reason?: string }>(
        `/voice/auto-call/cancel/${logId}`,
        undefined,
        { params: { organizationId } },
      )
      .then((res) => res.data),
};

export interface AutoCallStatus {
  id: string;
  disposition: string;
  triggerEvent: string;
  attemptNumber: number;
  phoneNumber: string;
  scheduledAt: string;
  createdAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  errorMessage: string | null;
  isPending: boolean;
  canRetry: boolean;
}
